import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  IconButton,
  Autocomplete,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  CheckCircle as CheckInIcon,
  People as PeopleIcon,
  HourglassEmpty as WaitingIcon,
  Assignment as InProgressIcon,
  Done as CompletedIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  HowToReg as HowToRegIcon,
  Visibility as VisibilityIcon,
  LocalHospital as LocalHospitalIcon,
  PointOfSale as PointOfSaleIcon,
} from '@mui/icons-material';
import { patientAPI, visitAPI, invoiceAPI } from '../services/api';
import StatCard from '../components/StatCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../constants/permissions';

const GradientStatCard = ({ title, value, icon, gradient, subtitle }) => (
  <Card 
    sx={{ 
      height: '100%',
      background: gradient,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: 'rgba(255,255,255,0.1)',
        borderRadius: '50%',
        transform: 'translate(30%, -30%)'
      }
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 1, p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 1.5, width: 32, height: 32 }}>
          {React.cloneElement(icon, { sx: { fontSize: 18 } })}
        </Avatar>
        <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const ReceptionPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [checkInStartTime, setCheckInStartTime] = useState(null);
  const [openTimeDialog, setOpenTimeDialog] = useState(false);
  const [selectedVisitForTime, setSelectedVisitForTime] = useState(null);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [openAppointmentDialog, setOpenAppointmentDialog] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({
    total: 0,
    checkedIn: 0,
    inProgress: 0,
    completed: 0
  });
  
  const [checkInForm, setCheckInForm] = useState({
    // Patient details (for new patients)
    name: '',
    age: '',
    gender: 'Male',
    dateOfBirth: '',
    sonWifeDaughterOf: '',
    maritalStatus: 'Married',
    profession: '',
    contact: { phone: '', email: '' },
    address: { street: '', city: '', state: '', pincode: '' },
    referralSource: '',
    medicalHistory: {
      diabetic: false,
      hypertensive: false,
      thyroid: false,
    },
    // Visit details (for all patients)
    chiefComplaint: '',
    vitalSigns: { bloodPressure: '', temperature: '', pulse: '' },
    referTo: 'optometrist', // Default referral: optometrist, doctor, or opd
    fees: {
      consultationFee: '',
      optometryFee: '',
      surgeryFee: '',
      investigationFee: '',
      otherFees: '',
      otherFeesDescription: ''
    }
  });

  const [appointmentForm, setAppointmentForm] = useState({
    patient: null,
    appointmentDate: '',
    appointmentTime: '',
    reason: '',
    notes: '',
  });

  useEffect(() => {
    fetchTodayVisits();
    fetchPatients();
  }, []);

  const fetchTodayVisits = async () => {
    try {
      setLoading(true);
      const response = await visitAPI.getTodayVisits();
      const visitsData = response.data.data;
      setVisits(visitsData);
      
      // Calculate stats
      const total = visitsData.length;
      const checkedIn = visitsData.filter(v => v.status === 'checked-in').length;
      const inProgress = visitsData.filter(v => 
        v.status.includes('in-') || v.status.includes('waiting-')
      ).length;
      const completed = visitsData.filter(v => v.status === 'completed').length;
      
      setStats({ total, checkedIn, inProgress, completed });
    } catch (error) {
      showSnackbar('Failed to fetch visits', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await patientAPI.getPatients({ limit: 100 });
      setPatients(response.data.data);
    } catch (error) {
      console.error('Failed to fetch patients');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Calculate waiting time between stages
  const calculateWaitingTime = (visit, afterStage) => {
    if (!visit.stages || visit.stages.length === 0) return null;
    
    const stages = visit.stages;
    const currentStageIndex = stages.findIndex(s => s.stage === afterStage);
    
    if (currentStageIndex === -1 || currentStageIndex === stages.length - 1) {
      // If stage not found or it's the last stage, check current status
      const lastStage = stages[stages.length - 1];
      if (lastStage.endTime && visit.status.includes('waiting')) {
        const waitStart = new Date(lastStage.endTime);
        const now = new Date();
        const waitSeconds = Math.floor((now - waitStart) / 1000);
        return waitSeconds;
      }
      return null;
    }
    
    const currentStage = stages[currentStageIndex];
    const nextStage = stages[currentStageIndex + 1];
    
    if (currentStage.endTime && nextStage.startTime) {
      const waitStart = new Date(currentStage.endTime);
      const waitEnd = new Date(nextStage.startTime);
      const waitSeconds = Math.floor((waitEnd - waitStart) / 1000);
      return waitSeconds;
    }
    
    return null;
  };

  const formatWaitTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleCheckIn = async () => {
    try {
      let patientId = selectedPatient?._id;

      // Calculate reception duration (time from dialog open to form submit)
      const receptionEndTime = new Date();
      const receptionDuration = checkInStartTime 
        ? Math.floor((receptionEndTime - checkInStartTime) / 1000) // in seconds
        : 0;

      // If new patient, register first
      if (isNewPatient) {
        const patientData = {
          name: checkInForm.name,
          age: checkInForm.age,
          gender: checkInForm.gender,
          dateOfBirth: checkInForm.dateOfBirth,
          sonWifeDaughterOf: checkInForm.sonWifeDaughterOf,
          maritalStatus: checkInForm.maritalStatus,
          profession: checkInForm.profession,
          contact: checkInForm.contact,
          address: checkInForm.address,
          referralSource: checkInForm.referralSource,
          medicalHistory: checkInForm.medicalHistory,
        };
        const patientResponse = await patientAPI.createPatient(patientData);
        patientId = patientResponse.data.data._id;
        showSnackbar('New patient registered!');
      }

      // Create visit for the patient with reception duration and referral
      const visitData = {
        patientId: patientId,
        chiefComplaint: checkInForm.chiefComplaint,
        vitalSigns: checkInForm.vitalSigns,
        receptionDuration: receptionDuration, // Send calculated duration
        referTo: checkInForm.referTo, // Send referral destination
        fees: {
          consultationFee: parseFloat(checkInForm.fees.consultationFee) || 0,
          optometryFee: parseFloat(checkInForm.fees.optometryFee) || 0,
          surgeryFee: parseFloat(checkInForm.fees.surgeryFee) || 0,
          investigationFee: parseFloat(checkInForm.fees.investigationFee) || 0,
          otherFees: parseFloat(checkInForm.fees.otherFees) || 0,
          otherFeesDescription: checkInForm.fees.otherFeesDescription
        }
      };
      
      console.log('Creating visit with data:', visitData);
      console.log('Refer To value:', checkInForm.referTo);
      
      await visitAPI.createVisit(visitData);
      
      showSnackbar(isNewPatient ? 'Patient registered and checked in!' : 'Patient checked in successfully!');
      setOpenCheckInDialog(false);
      resetForm();
      setCheckInStartTime(null); // Reset timer
      fetchTodayVisits();
      fetchPatients();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to check in patient', 'error');
    }
  };

  const resetForm = () => {
    setCheckInForm({
      name: '',
      age: '',
      gender: 'Male',
      dateOfBirth: '',
      sonWifeDaughterOf: '',
      maritalStatus: 'Married',
      profession: '',
      contact: { phone: '', email: '' },
      address: { street: '', city: '', state: '', pincode: '' },
      referralSource: '',
      medicalHistory: {
        diabetic: false,
        hypertensive: false,
        thyroid: false,
      },
      chiefComplaint: '',
      vitalSigns: { bloodPressure: '', temperature: '', pulse: '' },
      referTo: 'optometrist',
      fees: {
        consultationFee: '',
        optometryFee: '',
        surgeryFee: '',
        investigationFee: '',
        otherFees: '',
        otherFeesDescription: ''
      }
    });
    setSelectedPatient(null);
    setIsNewPatient(false);
    setSearchQuery('');
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setIsNewPatient(false);
    setCheckInForm({
      ...checkInForm,
      name: patient.name,
      age: patient.age,
      gender: patient.gender,
    });
  };

  const handleNewPatientClick = () => {
    setIsNewPatient(true);
    setSelectedPatient(null);
    setSearchQuery('');
  };

  const handleEndReception = async (visitId) => {
    try {
      await visitAPI.endStage(visitId, { notes: 'Reception completed' });
      showSnackbar('Reception stage completed!');
      fetchTodayVisits();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to end stage', 'error');
    }
  };

  const handleGenerateInvoice = async (visitId) => {
    try {
      const response = await invoiceAPI.generateInvoice(visitId);
      setInvoiceData(response.data.data);
      setOpenInvoiceDialog(true);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to generate invoice', 'error');
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    const colors = {
      'checked-in': 'info',
      'in-reception': 'primary',
      'waiting-optometrist': 'warning',
      'in-optometry': 'secondary',
      'waiting-doctor': 'warning',
      'in-consultation': 'secondary',
      'waiting-opd': 'warning',
      'in-opd': 'secondary',
      'completed': 'success',
    };
    return colors[status] || 'default';
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calculateElapsedTime = (startTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    return `${diffMins}m ${diffSecs}s`;
  };

  return (
    <Box>
      {/* Fixed Header Section with Stats */}
      <Paper 
        elevation={2}
        sx={{ 
          mb: 2, 
          p: 2,
          pt: 2.5,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'background.paper',
          borderRadius: 2
        }}
      >
        {/* Title Row */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight="700">
            Reception Portal
          </Typography>
        </Box>

        {/* Today's Status Row with Stats */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatCard label="Total Visits" value={stats.total} icon={<PeopleIcon fontSize="small" />} color="primary" />
          <StatCard label="Checked In" value={stats.checkedIn} icon={<CheckInIcon fontSize="small" />} color="info" />
          <StatCard label="In Progress" value={stats.inProgress} icon={<InProgressIcon fontSize="small" />} color="warning" />
          <StatCard label="Completed" value={stats.completed} icon={<CompletedIcon fontSize="small" />} color="success" />
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab icon={<CheckInIcon />} label="Check-Ins" iconPosition="start" />
            <Tab icon={<CalendarIcon />} label="Appointments" iconPosition="start" />
          </Tabs>
          <Box sx={{ pr: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => {
                setOpenCheckInDialog(true);
                setCheckInStartTime(new Date());
              }}
            >
              Patient Check-In
            </Button>
            <IconButton
              size="medium"
              onClick={fetchTodayVisits}
              sx={{
                border: '1px solid #E9E9F2',
                color: 'text.secondary',
                '&:hover': { bgcolor: '#F4F4F9' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Check-Ins Tab */}
      {tabValue === 0 && (
        <>

      {/* Patient List Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Today's Patient List
        </Typography>
      </Box>

      {/* Patient List Table */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Patient Details</strong></TableCell>
                <TableCell><strong>Visit Info</strong></TableCell>
                <TableCell><strong>Contact</strong></TableCell>
                <TableCell><strong>Chief Complaint</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Time Details</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No patients checked in today
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visits.map((visit, index) => (
                  <TableRow 
                    key={visit._id}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(79, 172, 254, 0.04)',
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: visit.patient?.gender === 'Male' ? 'primary.main' : 'secondary.main',
                            width: 40,
                            height: 40
                          }}
                        >
                          {visit.patient?.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {visit.patient?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {visit.patient?.patientId} • {visit.patient?.age}y • {visit.patient?.gender}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="600">
                          {visit.visitId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 14 }} />
                          {formatTime(visit.visitDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {visit.patient?.contact?.phone || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {visit.chiefComplaint || 'Not specified'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={visit.status.replace(/-/g, ' ')}
                        color={getStatusColor(visit.status)}
                        size="small"
                        sx={{ 
                          fontWeight: 'bold',
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {hasPermission(user, 'timer') && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ScheduleIcon />}
                          onClick={() => {
                            setSelectedVisitForTime(visit);
                            setOpenTimeDialog(true);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          View Times
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          startIcon={<ReceiptIcon />}
                          onClick={() => handleGenerateInvoice(visit._id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Invoice
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </>
      )}

      {/* Appointments Tab */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              Appointments
            </Typography>
            <Button
              variant="contained"
              startIcon={<CalendarIcon />}
              onClick={() => navigate('/appointments')}
            >
              Go to Appointments &amp; Queue
            </Button>
          </Box>

          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Full appointment booking, today's queue and check-in with token issuance is available on the dedicated Appointments page.
            </Typography>
            <Button variant="contained" startIcon={<CalendarIcon />} onClick={() => navigate('/appointments')}>
              Go to Appointments &amp; Queue
            </Button>
          </Paper>
        </Box>
      )}

      {/* Patient Check-In Dialog */}
      <Dialog 
        open={openCheckInDialog} 
        onClose={() => { 
          setOpenCheckInDialog(false); 
          resetForm(); 
          setCheckInStartTime(null); // Reset timer on cancel
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {isNewPatient ? 'New Patient Check-In' : selectedPatient ? `Check-In: ${selectedPatient.name}` : 'Patient Check-In'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Patient Search or New Patient */}
            {!isNewPatient && !selectedPatient && (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    options={patients}
                    getOptionLabel={(option) => `${option.name} - ${option.patientId} (${option.contact?.phone})`}
                    onChange={(e, newValue) => newValue && handlePatientSelect(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Search Existing Patient" placeholder="Search by name, ID, or phone" />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button fullWidth variant="outlined" onClick={handleNewPatientClick}>
                    + Register New Patient
                  </Button>
                </Grid>
              </>
            )}

            {/* New Patient Form */}
            {isNewPatient && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info">Registering a new patient</Alert>
                </Grid>
                
                {/* Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    value={checkInForm.name}
                    onChange={(e) => setCheckInForm({ ...checkInForm, name: e.target.value })}
                  />
                </Grid>

                {/* Age/Sex and Date of Birth */}
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Age"
                    type="number"
                    required
                    value={checkInForm.age}
                    onChange={(e) => setCheckInForm({ ...checkInForm, age: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    select
                    label="Sex"
                    required
                    value={checkInForm.gender}
                    onChange={(e) => setCheckInForm({ ...checkInForm, gender: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={checkInForm.dateOfBirth}
                    onChange={(e) => setCheckInForm({ ...checkInForm, dateOfBirth: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* S/o W/o D/o */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="S/o W/o D/o"
                    placeholder="Son of / Wife of / Daughter of"
                    value={checkInForm.sonWifeDaughterOf}
                    onChange={(e) => setCheckInForm({ ...checkInForm, sonWifeDaughterOf: e.target.value })}
                  />
                </Grid>

                {/* Marital Status */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Marital Status"
                    value={checkInForm.maritalStatus}
                    onChange={(e) => setCheckInForm({ ...checkInForm, maritalStatus: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="Married">Married</option>
                    <option value="Unmarried">Unmarried</option>
                  </TextField>
                </Grid>

                {/* Profession */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Profession"
                    value={checkInForm.profession}
                    onChange={(e) => setCheckInForm({ ...checkInForm, profession: e.target.value })}
                  />
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    value={checkInForm.address.street}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      address: { ...checkInForm.address, street: e.target.value }
                    })}
                  />
                </Grid>

                {/* Pin Code and Mobile No */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Pin Code"
                    value={checkInForm.address.pincode}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      address: { ...checkInForm.address, pincode: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile No."
                    required
                    value={checkInForm.contact.phone}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      contact: { ...checkInForm.contact, phone: e.target.value }
                    })}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={checkInForm.contact.email}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      contact: { ...checkInForm.contact, email: e.target.value }
                    })}
                  />
                </Grid>

                {/* Where did you get information about Nimbus From */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Where did you get the information about us?"
                    value={checkInForm.referralSource}
                    onChange={(e) => setCheckInForm({ ...checkInForm, referralSource: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="">Select source</option>
                    <option value="Just dial">Just dial</option>
                    <option value="Newspaper">Newspaper</option>
                    <option value="Pamphlet">Pamphlet</option>
                    <option value="Board">Board</option>
                    <option value="Friend or Relation">Friend or Relation</option>
                    <option value="Doctor Referral">Doctor Referral</option>
                  </TextField>
                </Grid>

                {/* Medical History */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                    Medical History
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={checkInForm.medicalHistory.diabetic}
                      onChange={(e) => setCheckInForm({
                        ...checkInForm,
                        medicalHistory: { ...checkInForm.medicalHistory, diabetic: e.target.checked }
                      })}
                      style={{ marginRight: 8 }}
                    />
                    <Typography>Diabetic</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={checkInForm.medicalHistory.hypertensive}
                      onChange={(e) => setCheckInForm({
                        ...checkInForm,
                        medicalHistory: { ...checkInForm.medicalHistory, hypertensive: e.target.checked }
                      })}
                      style={{ marginRight: 8 }}
                    />
                    <Typography>Hypertensive</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={checkInForm.medicalHistory.thyroid}
                      onChange={(e) => setCheckInForm({
                        ...checkInForm,
                        medicalHistory: { ...checkInForm.medicalHistory, thyroid: e.target.checked }
                      })}
                      style={{ marginRight: 8 }}
                    />
                    <Typography>Thyroid</Typography>
                  </Box>
                </Grid>
              </>
            )}

            {/* Visit Details (shown for both new and existing patients) */}
            {(isNewPatient || selectedPatient) && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                    Visit Details
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Chief Complaint"
                    multiline
                    rows={2}
                    placeholder="Why is the patient visiting today?"
                    value={checkInForm.chiefComplaint}
                    onChange={(e) => setCheckInForm({ ...checkInForm, chiefComplaint: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Refer To *</InputLabel>
                    <Select
                      value={checkInForm.referTo}
                      label="Refer To *"
                      onChange={(e) => setCheckInForm({ ...checkInForm, referTo: e.target.value })}
                    >
                      <MenuItem value="optometrist">👁️ Optometrist</MenuItem>
                      <MenuItem value="doctor">👨‍⚕️ Doctor</MenuItem>
                      <MenuItem value="opd">💊 OPD (Billing/Dispensing)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                    Patient will be sent to: <strong style={{ marginLeft: 4 }}>
                      {checkInForm.referTo === 'optometrist' ? 'Optometrist' : 
                       checkInForm.referTo === 'doctor' ? 'Doctor' : 'OPD'}
                    </strong>
                  </Alert>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Blood Pressure"
                    placeholder="120/80"
                    value={checkInForm.vitalSigns.bloodPressure}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      vitalSigns: { ...checkInForm.vitalSigns, bloodPressure: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Temperature"
                    placeholder="98.6°F"
                    value={checkInForm.vitalSigns.temperature}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      vitalSigns: { ...checkInForm.vitalSigns, temperature: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Pulse"
                    placeholder="72 bpm"
                    value={checkInForm.vitalSigns.pulse}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      vitalSigns: { ...checkInForm.vitalSigns, pulse: e.target.value }
                    })}
                  />
                </Grid>

                {/* Fee Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    💰 Fee Details
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Consultation Fee"
                    type="number"
                    placeholder="0"
                    value={checkInForm.fees.consultationFee}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      fees: { ...checkInForm.fees, consultationFee: e.target.value }
                    })}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Optometry Fee"
                    type="number"
                    placeholder="0"
                    value={checkInForm.fees.optometryFee}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      fees: { ...checkInForm.fees, optometryFee: e.target.value }
                    })}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Surgery Fee"
                    type="number"
                    placeholder="0"
                    value={checkInForm.fees.surgeryFee}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      fees: { ...checkInForm.fees, surgeryFee: e.target.value }
                    })}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Investigation/Tests Fee"
                    type="number"
                    placeholder="0"
                    value={checkInForm.fees.investigationFee}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      fees: { ...checkInForm.fees, investigationFee: e.target.value }
                    })}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other Fees"
                    type="number"
                    placeholder="0"
                    value={checkInForm.fees.otherFees}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      fees: { ...checkInForm.fees, otherFees: e.target.value }
                    })}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other Fees Description"
                    placeholder="e.g., Dilation drops, Special tests"
                    value={checkInForm.fees.otherFeesDescription}
                    onChange={(e) => setCheckInForm({
                      ...checkInForm,
                      fees: { ...checkInForm.fees, otherFeesDescription: e.target.value }
                    })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {(isNewPatient || selectedPatient) && (
            <Button onClick={() => { setIsNewPatient(false); setSelectedPatient(null); }} variant="outlined">
              Back
            </Button>
          )}
          <Button onClick={() => { 
            setOpenCheckInDialog(false); 
            resetForm(); 
            setCheckInStartTime(null); // Reset timer on cancel
          }} variant="outlined">
            Cancel
          </Button>
          {(isNewPatient || selectedPatient) && (
            <Button
              onClick={handleCheckIn}
              variant="contained"
            >
              {isNewPatient ? 'Register & Check-In' : 'Check-In'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Time Details Dialog */}
      <Dialog 
        open={openTimeDialog} 
        onClose={() => {
          setOpenTimeDialog(false);
          setSelectedVisitForTime(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Patient Journey Timeline
            </Typography>
          </Box>
          {selectedVisitForTime && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedVisitForTime.patient?.name} - {selectedVisitForTime.visitId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedVisitForTime && (
            <Box sx={{ py: 2 }}>
              {(() => {
                const stageConfig = {
                  reception: { color: '#7FAFD1', icon: <HowToRegIcon fontSize="small" />, label: 'Reception' },
                  optometrist: { color: '#7C84D4', icon: <VisibilityIcon fontSize="small" />, label: 'Optometrist' },
                  doctor: { color: '#8B90A7', icon: <LocalHospitalIcon fontSize="small" />, label: 'Doctor' },
                  opd: { color: '#6BBD9E', icon: <PointOfSaleIcon fontSize="small" />, label: 'OPD' },
                };
                const stages = selectedVisitForTime.stages || [];

                return (
                  <Box>
                    {stages.map((stage, index) => {
                      const nextStage = stages[index + 1];
                      const waitTime = stage.endTime && nextStage?.startTime
                        ? Math.floor((new Date(nextStage.startTime) - new Date(stage.endTime)) / 1000)
                        : stage.endTime && selectedVisitForTime.status.includes('waiting')
                        ? Math.floor((new Date() - new Date(stage.endTime)) / 1000)
                        : null;

                      const config = stageConfig[stage.stage] || { color: '#9B9AB0', icon: null, label: stage.stage };
                      const isLast = index === stages.length - 1;

                      return (
                        <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                          {/* Timeline rail */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 40, flexShrink: 0 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: `${config.color}22`, color: config.color }}>
                              {config.icon}
                            </Avatar>
                            {!isLast && (
                              <Box sx={{ width: 2, flexGrow: 1, my: 0.5, bgcolor: '#E9E9F2', minHeight: 24 }} />
                            )}
                          </Box>

                          {/* Stage content */}
                          <Box sx={{ flex: 1, pb: isLast ? 0 : 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {config.label}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  Stage {index + 1} of {stages.length}
                                </Typography>
                              </Typography>
                              <Chip
                                size="small"
                                label={stage.duration ? `${Math.floor(stage.duration / 60)}m ${stage.duration % 60}s` : 'In Progress'}
                                color={stage.duration ? 'success' : 'warning'}
                                variant={stage.duration ? 'filled' : 'outlined'}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">
                                <Box component="span" sx={{ color: '#9B9AB0', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', mr: 0.5 }}>Start</Box>
                                {stage.startTime ? new Date(stage.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <Box component="span" sx={{ color: '#9B9AB0', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', mr: 0.5 }}>End</Box>
                                {stage.endTime ? new Date(stage.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Ongoing'}
                              </Typography>
                            </Box>
                            {stage.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {stage.notes}
                              </Typography>
                            )}

                            {/* Waiting connector */}
                            {waitTime !== null && (
                              <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1, mt: 1.5,
                                px: 1.5, py: 1, borderRadius: 1.5,
                                bgcolor: '#F7F0E2', border: '1px dashed #DCB26E',
                              }}>
                                <WaitingIcon sx={{ fontSize: 14, color: '#C89D55' }} />
                                <Typography variant="caption" fontWeight={600} color="#C89D55">
                                  Waited {Math.floor(waitTime / 60)}m {waitTime % 60}s
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {nextStage ? `before ${stageConfig[nextStage.stage]?.label || nextStage.stage}` : '— currently waiting'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}

                    {/* Journey summary */}
                    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: '#FAFAFD', display: 'flex', gap: 4 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Total Active Time
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {(() => {
                            const totalActive = stages.reduce((sum, s) => sum + (s.duration || 0), 0);
                            return `${Math.floor(totalActive / 60)}m ${totalActive % 60}s`;
                          })()}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Current Status
                        </Typography>
                        <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                          {selectedVisitForTime.status.replace(/-/g, ' ')}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenTimeDialog(false);
            setSelectedVisitForTime(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Booking Dialog */}
      <Dialog
        open={openAppointmentDialog}
        onClose={() => {
          setOpenAppointmentDialog(false);
          setAppointmentForm({
            patient: null,
            appointmentDate: '',
            appointmentTime: '',
            reason: '',
            notes: '',
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Book Appointment
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={patients}
                getOptionLabel={(option) => `${option.name} (${option.patientId})`}
                value={appointmentForm.patient}
                onChange={(e, newValue) => setAppointmentForm({ ...appointmentForm, patient: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Patient *"
                    placeholder="Search patient..."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Appointment Date *"
                type="date"
                value={appointmentForm.appointmentDate}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, appointmentDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Appointment Time *"
                type="time"
                value={appointmentForm.appointmentTime}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, appointmentTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Visit *"
                value={appointmentForm.reason}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, reason: e.target.value })}
                placeholder="e.g., Eye checkup, Follow-up, Consultation"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                placeholder="Any special instructions or notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAppointmentDialog(false);
            setAppointmentForm({
              patient: null,
              appointmentDate: '',
              appointmentTime: '',
              reason: '',
              notes: '',
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // TODO: Implement appointment booking API
              showSnackbar('Appointment booked successfully!');
              setOpenAppointmentDialog(false);
              setAppointmentForm({
                patient: null,
                appointmentDate: '',
                appointmentTime: '',
                reason: '',
                notes: '',
              });
            }}
            disabled={!appointmentForm.patient || !appointmentForm.appointmentDate || !appointmentForm.appointmentTime || !appointmentForm.reason}
          >
            Book Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog
        open={openInvoiceDialog}
        onClose={() => setOpenInvoiceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Invoice</Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<PrintIcon />}
            onClick={handlePrintInvoice}
            size="small"
            className="no-print"
            sx={{ '@media print': { display: 'none' } }}
          >
            Print
          </Button>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {invoiceData && (
            <Box>
              {/* Clinic Header */}
              <Box sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  Eye Clinic Management System
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete Eye Care Solutions
                </Typography>
              </Box>

              {/* Invoice Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
                  <Typography variant="h6" fontWeight="bold">{invoiceData.invoiceNumber}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {new Date(invoiceData.invoiceDate).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>

              {/* Patient Info */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                  Patient Information
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Name:</strong> {invoiceData.patient.name}</Typography>
                    <Typography variant="body2"><strong>Patient ID:</strong> {invoiceData.patient.patientId}</Typography>
                    <Typography variant="body2"><strong>Age/Gender:</strong> {invoiceData.patient.age}y / {invoiceData.patient.gender}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2"><strong>Phone:</strong> {invoiceData.patient.phone}</Typography>
                    <Typography variant="body2"><strong>Visit ID:</strong> {invoiceData.visit.visitId}</Typography>
                    <Typography variant="body2"><strong>Visit Date:</strong> {new Date(invoiceData.visit.visitDate).toLocaleDateString()}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Items Table */}
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="right"><strong>GST %</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {item.description}
                          {item.specifications && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {item.specifications}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{item.gstRate ?? 0}%</TableCell>
                        <TableCell align="right">₹{item.totalPrice.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box sx={{ ml: 'auto', maxWidth: 300 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">₹{invoiceData.subtotal.toFixed(2)}</Typography>
                </Box>
                {invoiceData.discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Discount:</Typography>
                    <Typography variant="body2">-₹{invoiceData.discount.toFixed(2)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Tax (GST):</Typography>
                  <Typography variant="body2">₹{invoiceData.tax.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold">Total Amount:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ₹{invoiceData.totalAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="success.main">Paid:</Typography>
                  <Typography variant="body2" color="success.main">₹{(invoiceData.paidAmount || 0).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="error.main" fontWeight="bold">Balance Due:</Typography>
                  <Typography variant="body2" color="error.main" fontWeight="bold">₹{(invoiceData.balanceAmount || 0).toFixed(2)}</Typography>
                </Box>
              </Box>

              {/* Consultation Details */}
              {invoiceData.consultation && (
                <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                    Consultation Details
                  </Typography>
                  <Typography variant="body2"><strong>Doctor:</strong> {invoiceData.doctor}</Typography>
                  <Typography variant="body2"><strong>Diagnosis:</strong> {invoiceData.consultation.diagnosis}</Typography>
                  {invoiceData.consultation.prescriptions?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Prescriptions:</Typography>
                      {invoiceData.consultation.prescriptions.map((p, i) => (
                        <Typography key={i} variant="caption" display="block" sx={{ ml: 2 }}>
                          • {p.medicine} - {p.dosage}, {p.frequency}, {p.duration}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'grey.300', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Thank you for choosing our services!
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  For any queries, please contact us at the reception.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions className="no-print" sx={{ '@media print': { display: 'none' } }}>
          <Button onClick={() => setOpenInvoiceDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceptionPortal;
